/**
 * Định dạng tiền của dòng giá từ bảng tính: tối đa 3 chữ số thập phân, bỏ số 0 thừa,
 * dấu phẩy thập phân và dấu chấm hàng nghìn kiểu Việt Nam (50,245; 38.220,56; 188).
 */
export function formatSheetAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return ''
  const rounded = Math.round(value * 1000) / 1000
  const negative = rounded < 0
  const [whole, fraction = ''] = Math.abs(rounded).toFixed(3).split('.')
  const trimmed = fraction.replace(/0+$/, '')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  const body = trimmed ? `${grouped},${trimmed}` : grouped
  return negative && body !== '0' ? `-${body}` : body
}

/**
 * Đọc số tiền nhập tay theo cùng quy ước với formatSheetAmount: dấu chấm là dấu nghìn, dấu phẩy là dấu thập phân
 * (215.000 → 215000; 38.220,56 → 38220.56; 9,5 → 9.5). Trả về null khi không phải số.
 * Không dùng parseFloat trực tiếp: "2.150" sẽ thành 2,15 và làm hỏng số đang gõ.
 */
export function parseSheetAmount(text: string | null | undefined): number | null {
  if (text === null || text === undefined) return null
  let s = String(text).replace(/[\s\u00a0₫đ$]/gi, '')
  if (s === '' || s === '-' || s === ',' || s === '.') return null
  const negative = s.startsWith('-')
  s = s.replace(/^[+-]/, '')

  const dotCount = (s.match(/\./g) || []).length
  const commaCount = (s.match(/,/g) || []).length

  let intPart = ''
  let fracPart = ''

  if (dotCount > 0 && commaCount > 0) {
    const lastDot = s.lastIndexOf('.')
    const lastComma = s.lastIndexOf(',')
    if (lastComma > lastDot) {
      intPart = s.slice(0, lastComma).replace(/\./g, '')
      fracPart = s.slice(lastComma + 1).replace(/[.,]/g, '')
    } else {
      intPart = s.slice(0, lastDot).replace(/,/g, '')
      fracPart = s.slice(lastDot + 1).replace(/[.,]/g, '')
    }
  } else if (commaCount > 1) {
    intPart = s.replace(/,/g, '')
  } else if (dotCount > 1) {
    intPart = s.replace(/\./g, '')
  } else if (commaCount === 1) {
    const comma = s.indexOf(',')
    intPart = s.slice(0, comma).replace(/\./g, '')
    fracPart = s.slice(comma + 1).replace(/[.,]/g, '')
  } else if (dotCount === 1) {
    const dot = s.indexOf('.')
    const left = s.slice(0, dot)
    const right = s.slice(dot + 1)
    // While typing a grouped amount, a fourth digit may be appended to the
    // first group (e.g. `2.1500`), so keep treating 3+ digits after the dot
    // as a thousands group unless the number starts with 0.
    if (left !== '0' && right.length >= 3 && /^\d+$/.test(right)) {
      intPart = (left + right).replace(/\./g, '')
    } else {
      intPart = left
      fracPart = right
    }
  } else {
    intPart = s
  }

  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart)) return null
  if (intPart === '' && fracPart === '') return null
  const n = Number(`${intPart || '0'}${fracPart ? `.${fracPart}` : ''}`)
  if (!Number.isFinite(n)) return null
  return negative ? -n : n
}
